import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Alert,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Stack,
  Autocomplete,
  IconButton,
  Tooltip,
  Divider,
  InputAdornment,
} from '@mui/material';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import RestoreFromTrashOutlinedIcon from '@mui/icons-material/RestoreFromTrashOutlined';
import SelectAllOutlinedIcon from '@mui/icons-material/SelectAllOutlined';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import Checkbox from '@mui/material/Checkbox';
import axios from 'axios';
import { getApiBaseUrl, getStoredToken } from '../../services/httpClient';
import {
  CATALOG_THUMB_MAX_INPUT_BYTES,
  CATALOG_THUMB_PX,
  compressImageForUpload,
  toOptimizedThumbDeliveryUrl,
} from '../../utils/compressImageUpload';

const api = axios.create({
  baseURL: `${getApiBaseUrl()}/taxonomy`,
  // Remote DO DB from localhost can be slower on cold ensure; keep under a minute.
  timeout: 45000,
});

api.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.headers['Content-Type'] = 'application/json';
  return config;
});

const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'dml5dugde';
const CLOUDINARY_UPLOAD_PRESET =
  process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'locsy_business_profiles';
const THUMB_PX = CATALOG_THUMB_PX;
const UNIT_PRESETS = ['1 kg', '1 L', '1 piece', '1 dozen', '1 pack', 'per plate', 'per box'];
const GST_PRESETS = [0, 5, 12, 18, 28];

/** List → discount → GST-exclusive final (industry retail / billing). */
const calcPriceBreakdown = (price, discountPercent, gstPercent) => {
  const list = Math.max(0, Number(price) || 0);
  const disc = Math.min(100, Math.max(0, Number(discountPercent) || 0));
  const gst = Math.max(0, Number(gstPercent) || 0);
  const afterDiscount = Math.round(list * (1 - disc / 100) * 100) / 100;
  const gstAmount = Math.round(afterDiscount * (gst / 100) * 100) / 100;
  const finalPrice = Math.round((afterDiscount + gstAmount) * 100) / 100;
  return { list, disc, gst, afterDiscount, gstAmount, finalPrice };
};

const buildThumbnailPrompt = ({ name, details, itemType }) => {
  const label = itemType === 'service' ? 'service' : 'product';
  return `Create ONE square marketplace thumbnail for a local Indian ${label} catalog card.

Name: ${String(name || '').trim() || '(unnamed)'}
Details: ${String(details || '').trim() || 'N/A'}

STRICT OUTPUT (thumbnail only — do not invent other sizes or variants):
- Exact canvas: ${THUMB_PX}×${THUMB_PX} pixels (not 1024, not 2K, not 4K)
- Format: PNG with transparent background (no studio floor, no grey backdrop, alpha = 0 outside subject)
- File size target: under 150 KB (web thumbnail). Prefer flat lighting and limited detail so PNG stays small.
- Framing: single centered subject, slight padding, crisp silhouette on transparency
- No text, logos, watermarks, price tags, utensils, napkins, or busy props
- Photo-realistic, high contrast, readable at ~80px on a phone list
- Output ONLY this one ${THUMB_PX}×${THUMB_PX} transparent PNG (do not attach a second opaque version).`;
};

/** Every upload is auto-resized+compressed in the browser, then sent to Cloudinary. */
const uploadProductThumbnail = async (file) => {
  if (!CLOUDINARY_CLOUD_NAME || !CLOUDINARY_UPLOAD_PRESET) {
    throw new Error('Cloudinary is not configured.');
  }
  const prepared = await compressImageForUpload(file);
  const formData = new FormData();
  formData.append('file', prepared);
  formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
  formData.append('folder', 'catalog_product_thumbnails');
  const cleanAxios = axios.create();
  if (cleanAxios.defaults.headers.common) {
    delete cleanAxios.defaults.headers.common.Authorization;
    delete cleanAxios.defaults.headers.common.authorization;
  }
  const response = await cleanAxios.post(
    `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
    formData,
  );
  const secureUrl = response?.data?.secure_url || response?.data?.url || '';
  if (!secureUrl) throw new Error('Thumbnail upload failed');
  return toOptimizedThumbDeliveryUrl(secureUrl, THUMB_PX);
};

const CatalogTemplates = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [indexCategories, setIndexCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  /** business_category_index.category_key */
  const [selectedIndexKey, setSelectedIndexKey] = useState('');
  /** catalog_categories.code — used for template CRUD APIs */
  const [selectedCategory, setSelectedCategory] = useState('');
  const [binding, setBinding] = useState(null);
  const [detail, setDetail] = useState(null);
  const [subFilter, setSubFilter] = useState('all');
  const initialKeyHandled = useRef(false);
  const [snack, setSnack] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  /** @type {'add' | 'edit'} */
  const [dialogMode, setDialogMode] = useState('add');
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    subcategory_code: '',
    group_id: '',
    item_type: 'product',
    name_en: '',
    product_details: '',
    default_unit: '',
    price: '',
    discount_percent: '',
    gst_percent: '',
    hsn_code: '',
    challana: '',
    thumbnail_url: '',
    main_category: '',
    sub_category: '',
    display_order: '',
  });
  const [aiPrompt, setAiPrompt] = useState('');
  const [thumbUploading, setThumbUploading] = useState(false);
  const thumbInputRef = useRef(null);
  const [itemSearch, setItemSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [showRecycleBin, setShowRecycleBin] = useState(false);
  const [binBusy, setBinBusy] = useState(false);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [groupForm, setGroupForm] = useState({
    name: '',
    item_type: 'service',
    default_unit: '',
    subcategory_code: '',
  });
  const [groupSaving, setGroupSaving] = useState(false);

  const loadIndexCategories = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/admin/index-categories');
      if (res.data?.success) {
        const data = res.data.data || [];
        setIndexCategories(data);
      } else {
        setError(res.data?.message || 'Failed to load business categories');
      }
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadEditorForIndexKey = useCallback(async (categoryKey, { defaultSub } = {}) => {
    if (!categoryKey) return;
    setError(null);
    try {
      const res = await api.post(
        `/admin/ensure-from-index/${encodeURIComponent(categoryKey)}`,
      );
      if (!res.data?.success) {
        setError(res.data?.message || 'Failed to open template');
        return;
      }
      const nextBinding = res.data.data?.binding || null;
      const editor = res.data.data?.editor || null;
      setBinding(nextBinding);
      setSelectedIndexKey(categoryKey);
      setSelectedCategory(nextBinding?.catalogCategoryCode || '');
      setDetail(editor);
      setSubFilter(defaultSub || nextBinding?.subcategoryCode || 'all');
      setSearchParams({ categoryKey }, { replace: true });
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to load template');
    }
  }, [setSearchParams]);

  useEffect(() => {
    loadIndexCategories();
  }, [loadIndexCategories]);

  useEffect(() => {
    if (loading || !indexCategories.length || initialKeyHandled.current) return;
    const fromUrl = searchParams.get('categoryKey');
    initialKeyHandled.current = true;
    const key =
      fromUrl && indexCategories.some((c) => c.category_key === fromUrl)
        ? fromUrl
        : indexCategories[0]?.category_key;
    if (key) {
      loadEditorForIndexKey(key);
    }
  }, [loading, indexCategories, searchParams, loadEditorForIndexKey]);

  const handleSelectIndexCategory = (categoryKey) => {
    if (!categoryKey || categoryKey === selectedIndexKey) return;
    loadEditorForIndexKey(categoryKey);
  };

  /** Keep URL + index key aligned with the shop-type filter (furniture vs kirana). */
  const applySubFilter = (nextSub) => {
    const code = nextSub || 'all';
    setSubFilter(code);
    const mainId =
      binding?.mainCategoryId ||
      String(selectedIndexKey || '').split('__')[0] ||
      selectedCategory ||
      '';
    if (mainId && code && code !== 'all') {
      const nextKey = `${mainId}__${code}`;
      setSelectedIndexKey(nextKey);
      setSearchParams({ categoryKey: nextKey }, { replace: true });
      const match = indexCategories.find((c) => c.category_key === nextKey);
      if (match) {
        setBinding((prev) =>
          prev
            ? {
                ...prev,
                businessCategoryKey: nextKey,
                subcategoryCode: code,
                label: match.label || prev.label,
              }
            : prev,
        );
      }
    }
  };

  const refreshEditor = useCallback(async () => {
    if (!selectedCategory) return;
    const res = await api.get(`/admin/categories/${encodeURIComponent(selectedCategory)}`);
    if (res.data?.success) setDetail(res.data.data);
  }, [selectedCategory]);

  const sortedIndexCategories = useMemo(() => {
    return [...indexCategories].sort((a, b) =>
      String(a.label || '').localeCompare(String(b.label || ''), undefined, {
        sensitivity: 'base',
      }),
    );
  }, [indexCategories]);

  const subsForCategory = useMemo(() => {
    const subs = detail?.subcategories || [];
    return [...subs].sort((a, b) =>
      String(a.name_en || a.code || '').localeCompare(
        String(b.name_en || b.code || ''),
        undefined,
        { sensitivity: 'base' },
      ),
    );
  }, [detail]);

  const groupsForSub = useMemo(() => {
    const groups = (detail?.groups || []).filter((g) => g.is_active !== false);
    const sub =
      form.subcategory_code ||
      (subFilter !== 'all' ? subFilter : '') ||
      '';
    const scoped = sub
      ? groups.filter(
          (g) =>
            String(g.subcategory_code || '').toLowerCase() ===
            String(sub).toLowerCase(),
        )
      : groups;
    return [...scoped].sort((a, b) =>
      String(a.name || '').localeCompare(String(b.name || ''), undefined, {
        sensitivity: 'base',
      }),
    );
  }, [detail, form.subcategory_code, subFilter]);

  const filteredItems = useMemo(() => {
    const rawItems = detail?.items || [];
    const rawTrashed = detail?.trashedItems;
    // Prefer API split; fall back for older backends that still return inactive rows in items.
    const active =
      rawTrashed != null
        ? rawItems
        : rawItems.filter((i) => i.is_active !== false);
    const trashed =
      rawTrashed != null
        ? rawTrashed
        : rawItems.filter((i) => i.is_active === false);
    const items = showRecycleBin ? trashed : active;
    const bySub =
      subFilter === 'all'
        ? items
        : items.filter(
            (i) =>
              String(i.subcategory_code || '').toLowerCase() ===
              String(subFilter || '').toLowerCase(),
          );
    const q = itemSearch.trim().toLowerCase();
    if (!q) return bySub;
    return bySub.filter((i) => {
      const hay = [
        i.name_en,
        i.product_details,
        i.item_type,
        i.main_category,
        i.sub_category,
        i.subcategory_code,
        i.default_unit,
        i.hsn_code,
      ]
        .map((v) => String(v || '').toLowerCase())
        .join(' ');
      return hay.includes(q);
    });
  }, [detail, subFilter, itemSearch, showRecycleBin]);

  const trashedCount = useMemo(() => {
    if (detail?.trashedItems != null) return detail.trashedItems.length;
    return (detail?.items || []).filter((i) => i.is_active === false).length;
  }, [detail]);

  const allVisibleSelected =
    filteredItems.length > 0 && filteredItems.every((row) => selectedIds.has(row.id));

  const toggleSelectAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        filteredItems.forEach((row) => next.delete(row.id));
      } else {
        filteredItems.forEach((row) => next.add(row.id));
      }
      return next;
    });
  };

  const toggleSelectRow = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  useEffect(() => {
    // Drop selections that are no longer in the current editor payload.
    const pool = [
      ...(detail?.items || []),
      ...(detail?.trashedItems || []),
    ];
    const valid = new Set(pool.map((i) => i.id));
    setSelectedIds((prev) => {
      const next = new Set([...prev].filter((id) => valid.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [detail]);

  useEffect(() => {
    clearSelection();
  }, [subFilter, selectedCategory, showRecycleBin]);

  const emptyForm = () => ({
    subcategory_code: '',
    group_id: '',
    item_type: 'product',
    name_en: '',
    product_details: '',
    default_unit: '',
    price: '',
    discount_percent: '',
    gst_percent: '',
    hsn_code: '',
    challana: '',
    thumbnail_url: '',
    main_category: '',
    sub_category: '',
    display_order: '',
  });

  const priceBreakdown = useMemo(
    () => calcPriceBreakdown(form.price, form.discount_percent, form.gst_percent),
    [form.price, form.discount_percent, form.gst_percent],
  );

  const applyGroupDefaults = (groupId, prev = {}) => {
    const group = (detail?.groups || []).find((g) => String(g.id) === String(groupId));
    if (!group) {
      return { ...prev, group_id: groupId || '' };
    }
    return {
      ...prev,
      group_id: group.id,
      main_category: group.name || '',
      item_type: group.item_type === 'service' ? 'service' : 'product',
      default_unit:
        prev.default_unit && String(prev.default_unit).trim()
          ? prev.default_unit
          : group.default_unit || '',
      subcategory_code: group.subcategory_code || prev.subcategory_code || '',
    };
  };

  const openAdd = () => {
    setDialogMode('add');
    setEditingId(null);
    setAiPrompt('');
    const subCode = subFilter !== 'all' ? subFilter : subsForCategory[0]?.code || '';
    const scopedGroups = (detail?.groups || []).filter(
      (g) =>
        g.is_active !== false &&
        (!subCode ||
          String(g.subcategory_code || '').toLowerCase() === String(subCode).toLowerCase()),
    );
    const firstGroup = scopedGroups[0];
    setForm(
      applyGroupDefaults(firstGroup?.id || '', {
        ...emptyForm(),
        subcategory_code: subCode,
      }),
    );
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    const defPricing =
      row.pricing?.find((p) => p.region_code === 'DEFAULT') || row.pricing?.[0] || {};
    const listPrice =
      defPricing.max_price != null && Number(defPricing.max_price) > 0
        ? defPricing.max_price
        : defPricing.median_price;
    setDialogMode('edit');
    setEditingId(row.id);
    setAiPrompt('');
    const matchedGroup =
      (detail?.groups || []).find((g) => String(g.id) === String(row.group_id)) ||
      (detail?.groups || []).find(
        (g) =>
          String(g.subcategory_code || '').toLowerCase() ===
            String(row.subcategory_code || '').toLowerCase() &&
          String(g.name || '').toLowerCase() ===
            String(row.main_category || '').toLowerCase(),
      );
    setForm({
      subcategory_code: row.subcategory_code || '',
      group_id: matchedGroup?.id || row.group_id || '',
      item_type: row.item_type || 'product',
      name_en: row.name_en || '',
      product_details: row.product_details || '',
      default_unit: row.default_unit || '',
      price: listPrice != null ? String(listPrice) : '',
      discount_percent:
        row.discount_percent != null ? String(row.discount_percent) : '',
      gst_percent: row.gst_percent != null ? String(row.gst_percent) : '',
      hsn_code: row.hsn_code || '',
      challana: row.challana != null && Number(row.challana) !== 0 ? String(row.challana) : '',
      thumbnail_url: row.thumbnail_url || '',
      main_category: row.main_category || matchedGroup?.name || '',
      sub_category: row.sub_category || '',
      display_order: row.display_order != null ? String(row.display_order) : '',
    });
    setDialogOpen(true);
  };

  const openCreateGroup = () => {
    setGroupForm({
      name: '',
      item_type: 'service',
      default_unit: '',
      subcategory_code: subFilter !== 'all' ? subFilter : form.subcategory_code || subsForCategory[0]?.code || '',
    });
    setGroupDialogOpen(true);
  };

  const submitCreateGroup = async () => {
    if (!selectedCategory) return;
    if (!groupForm.name?.trim() || !groupForm.subcategory_code) {
      setSnack({ severity: 'warning', message: 'Group name and shop type are required.' });
      return;
    }
    if (groupForm.item_type === 'product' && !String(groupForm.default_unit || '').trim()) {
      setSnack({ severity: 'warning', message: 'Default unit is required for product groups.' });
      return;
    }
    setGroupSaving(true);
    try {
      const res = await api.post(
        `/admin/categories/${encodeURIComponent(selectedCategory)}/template-groups`,
        {
          name: groupForm.name.trim(),
          subcategory_code: groupForm.subcategory_code,
          item_type: groupForm.item_type,
          default_unit: groupForm.default_unit?.trim() || null,
        },
      );
      if (res.data?.success) {
        setDetail(res.data.data);
        const created = res.data.group;
        if (created?.id) {
          setForm((f) => applyGroupDefaults(created.id, f));
        }
        setGroupDialogOpen(false);
        setSnack({ severity: 'success', message: `Group "${groupForm.name.trim()}" created.` });
      } else {
        setSnack({ severity: 'error', message: res.data?.message || 'Could not create group' });
      }
    } catch (e) {
      setSnack({
        severity: 'error',
        message: e.response?.data?.message || e.message || 'Could not create group',
      });
    } finally {
      setGroupSaving(false);
    }
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
    setDialogMode('add');
    setAiPrompt('');
    setThumbUploading(false);
  };

  const handleGeneratePrompt = () => {
    if (!form.name_en?.trim()) {
      setSnack({ severity: 'warning', message: 'Enter a display name first to generate the prompt.' });
      return;
    }
    const prompt = buildThumbnailPrompt({
      name: form.name_en,
      details: form.product_details,
      itemType: form.item_type,
    });
    setAiPrompt(prompt);
    setSnack({
      severity: 'success',
      message: 'Prompt ready — copy it, then open Gemini or ChatGPT.',
    });
  };

  const handleCopyPrompt = async () => {
    const text =
      aiPrompt ||
      buildThumbnailPrompt({
        name: form.name_en,
        details: form.product_details,
        itemType: form.item_type,
      });
    if (!form.name_en?.trim()) {
      setSnack({ severity: 'warning', message: 'Enter a display name first.' });
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      if (!aiPrompt) setAiPrompt(text);
      setSnack({ severity: 'success', message: 'Prompt copied to clipboard.' });
    } catch {
      setSnack({ severity: 'error', message: 'Could not copy — select the prompt and copy manually.' });
    }
  };

  const handleThumbFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setSnack({ severity: 'warning', message: 'Please choose an image file (PNG preferred).' });
      return;
    }
    if (file.size > CATALOG_THUMB_MAX_INPUT_BYTES) {
      setSnack({
        severity: 'warning',
        message: 'Image is over 12 MB — pick a smaller file (upload still auto-compresses to 512×512).',
      });
      return;
    }
    setThumbUploading(true);
    try {
      const url = await uploadProductThumbnail(file);
      setForm((f) => ({ ...f, thumbnail_url: url }));
      setSnack({
        severity: 'success',
        message: `Thumbnail resized to ${THUMB_PX}×${THUMB_PX} and uploaded.`,
      });
    } catch (err) {
      setSnack({
        severity: 'error',
        message: err.message || 'Thumbnail upload failed',
      });
    } finally {
      setThumbUploading(false);
    }
  };

  const buildPricingPayload = () => {
    const { list, afterDiscount, finalPrice } = calcPriceBreakdown(
      form.price,
      form.discount_percent,
      form.gst_percent,
    );
    return {
      // Backward-compatible band: list / pre-GST / final (suggested for Smart Bill)
      max_price: list,
      min_price: afterDiscount,
      median_price: finalPrice,
    };
  };

  const submitAdd = async () => {
    if (!form.name_en?.trim() || !form.subcategory_code) {
      setSnack({ severity: 'warning', message: 'Name and shop type are required.' });
      return;
    }
    if (!form.group_id) {
      setSnack({
        severity: 'warning',
        message: 'Create / select a category group first (e.g. Print Services).',
      });
      return;
    }
    if (!String(form.default_unit || '').trim()) {
      setSnack({ severity: 'warning', message: 'Unit is required.' });
      return;
    }
    if (form.price === '' || Number.isNaN(Number(form.price))) {
      setSnack({ severity: 'warning', message: 'Enter a valid price.' });
      return;
    }
    try {
      const pricing = buildPricingPayload();
      const selectedGroup = (detail?.groups || []).find(
        (g) => String(g.id) === String(form.group_id),
      );
      await api.post(`/admin/categories/${encodeURIComponent(selectedCategory)}/template-items`, {
        item_type: form.item_type,
        name_en: form.name_en.trim(),
        subcategory_code: form.subcategory_code,
        group_id: form.group_id,
        main_category: selectedGroup?.name || form.main_category || undefined,
        sub_category: form.sub_category || undefined,
        default_unit: form.default_unit.trim(),
        product_details: form.product_details?.trim() || null,
        thumbnail_url: form.thumbnail_url || null,
        discount_percent: form.discount_percent === '' ? null : Number(form.discount_percent),
        gst_percent: form.gst_percent === '' ? null : Number(form.gst_percent),
        hsn_code: form.hsn_code || null,
        challana: form.challana === '' ? 0 : Number(form.challana),
        ...pricing,
        region_code: 'DEFAULT',
        source: 'catalog_templates_ui',
        is_active: true,
      });
      setSnack({ severity: 'success', message: 'Template line added.' });
      closeDialog();
      const res = await api.get(`/admin/categories/${encodeURIComponent(selectedCategory)}`);
      if (res.data?.success) setDetail(res.data.data);
    } catch (e) {
      setSnack({
        severity: 'error',
        message: e.response?.data?.message || e.message || 'Save failed',
      });
    }
  };

  const submitEdit = async () => {
    if (!editingId || !form.name_en?.trim() || !form.subcategory_code) {
      setSnack({ severity: 'warning', message: 'Name and shop type are required.' });
      return;
    }
    if (!form.group_id) {
      setSnack({
        severity: 'warning',
        message: 'Create / select a category group first (e.g. Print Services).',
      });
      return;
    }
    if (!String(form.default_unit || '').trim()) {
      setSnack({ severity: 'warning', message: 'Unit is required.' });
      return;
    }
    if (form.price === '' || Number.isNaN(Number(form.price))) {
      setSnack({ severity: 'warning', message: 'Enter a valid price.' });
      return;
    }
    try {
      const pricing = buildPricingPayload();
      const selectedGroup = (detail?.groups || []).find(
        (g) => String(g.id) === String(form.group_id),
      );
      await api.put(`/admin/template-items/${editingId}`, {
        item_type: form.item_type,
        name_en: form.name_en.trim(),
        subcategory_code: form.subcategory_code,
        group_id: form.group_id,
        main_category: selectedGroup?.name || form.main_category || undefined,
        sub_category: form.sub_category || undefined,
        default_unit: form.default_unit.trim(),
        product_details: form.product_details?.trim() || null,
        thumbnail_url: form.thumbnail_url || null,
        discount_percent: form.discount_percent === '' ? null : Number(form.discount_percent),
        gst_percent: form.gst_percent === '' ? null : Number(form.gst_percent),
        hsn_code: form.hsn_code || null,
        challana: form.challana === '' ? 0 : Number(form.challana),
        display_order: form.display_order === '' ? undefined : Number(form.display_order),
        is_active: true,
        region_code: 'DEFAULT',
        ...pricing,
        source: 'catalog_templates_ui',
      });
      setSnack({ severity: 'success', message: 'Template line updated.' });
      closeDialog();
      const res = await api.get(`/admin/categories/${encodeURIComponent(selectedCategory)}`);
      if (res.data?.success) setDetail(res.data.data);
    } catch (e) {
      setSnack({
        severity: 'error',
        message: e.response?.data?.message || e.message || 'Update failed',
      });
    }
  };

  const deleteLine = async (id, name) => {
    if (
      !window.confirm(
        `Move "${name}" to Recycle Bin?\n\nIt will be hidden from new shop imports. You can restore it or delete forever from the Recycle Bin.`,
      )
    ) {
      return;
    }
    try {
      await api.delete(`/admin/template-items/${id}`);
      setSnack({ severity: 'success', message: 'Moved to Recycle Bin.' });
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      const res = await api.get(`/admin/categories/${encodeURIComponent(selectedCategory)}`);
      if (res.data?.success) setDetail(res.data.data);
    } catch (e) {
      setSnack({ severity: 'error', message: e.response?.data?.message || e.message });
    }
  };

  const deleteSelectedLines = async () => {
    const ids = [...selectedIds];
    if (!ids.length || bulkDeleting) return;
    if (
      !window.confirm(
        `Move ${ids.length} selected line${ids.length === 1 ? '' : 's'} to Recycle Bin?\n\nThey will be hidden from new imports. Restore or permanently delete them from the Recycle Bin.`,
      )
    ) {
      return;
    }
    setBulkDeleting(true);
    let deleted = 0;
    let failed = 0;
    try {
      for (const id of ids) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await api.delete(`/admin/template-items/${id}`);
          deleted += 1;
        } catch {
          failed += 1;
        }
      }
      clearSelection();
      const res = await api.get(`/admin/categories/${encodeURIComponent(selectedCategory)}`);
      if (res.data?.success) setDetail(res.data.data);
      setSnack({
        severity: failed ? 'warning' : 'success',
        message: failed
          ? `Moved ${deleted}, failed ${failed}.`
          : `Moved ${deleted} line${deleted === 1 ? '' : 's'} to Recycle Bin.`,
      });
    } finally {
      setBulkDeleting(false);
    }
  };

  const restoreSelectedLines = async () => {
    const ids = [...selectedIds];
    if (!ids.length || binBusy) return;
    setBinBusy(true);
    let ok = 0;
    let failed = 0;
    try {
      for (const id of ids) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await api.post(`/admin/template-items/${id}/restore`);
          ok += 1;
        } catch {
          failed += 1;
        }
      }
      clearSelection();
      const res = await api.get(`/admin/categories/${encodeURIComponent(selectedCategory)}`);
      if (res.data?.success) setDetail(res.data.data);
      setSnack({
        severity: failed ? 'warning' : 'success',
        message: failed
          ? `Restored ${ok}, failed ${failed}.`
          : `Restored ${ok} line${ok === 1 ? '' : 's'}.`,
      });
    } finally {
      setBinBusy(false);
    }
  };

  const purgeSelectedLines = async () => {
    const ids = [...selectedIds];
    if (!ids.length || binBusy) return;
    if (
      !window.confirm(
        `Permanently delete ${ids.length} line${ids.length === 1 ? '' : 's'}?\n\nThis cannot be undone.`,
      )
    ) {
      return;
    }
    setBinBusy(true);
    let ok = 0;
    let failed = 0;
    try {
      for (const id of ids) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await api.delete(`/admin/template-items/${id}?permanent=true`);
          ok += 1;
        } catch {
          failed += 1;
        }
      }
      clearSelection();
      const res = await api.get(`/admin/categories/${encodeURIComponent(selectedCategory)}`);
      if (res.data?.success) setDetail(res.data.data);
      setSnack({
        severity: failed ? 'warning' : 'success',
        message: failed
          ? `Deleted ${ok}, failed ${failed}.`
          : `Permanently deleted ${ok} line${ok === 1 ? '' : 's'}.`,
      });
    } finally {
      setBinBusy(false);
    }
  };

  const selectedIndexRow = useMemo(
    () => indexCategories.find((c) => c.category_key === selectedIndexKey),
    [indexCategories, selectedIndexKey],
  );

  if (loading && !indexCategories.length) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <Inventory2OutlinedIcon color="primary" sx={{ fontSize: 40 }} />
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Catalog templates (Smart Bill)
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Default price lists per shop type from the business category index — businesses add missing lines only via Load from Template.
          </Typography>
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Each <strong>shop type (sub)</strong> has its own product list under a main category.
        Example: Furniture tester shares → only <code>general_daily_shops__furniture</code>;
        Kirana stays separate. Filter by shop type below to view that list. Mitra Beta Testers
        share into their registered shop type only; regular Mitras import with Update Template.
      </Alert>

      {selectedIndexRow && binding && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Editing template for <strong>{selectedIndexRow.label}</strong> — catalog{' '}
          <code>{binding.catalogCategoryCode}</code> / sub <code>{binding.subcategoryCode}</code>
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Shop / service type (index)
              </Typography>
              <Autocomplete
                size="small"
                fullWidth
                autoHighlight
                options={sortedIndexCategories}
                value={
                  sortedIndexCategories.find((c) => c.category_key === selectedIndexKey) || null
                }
                onChange={(_, newValue) => {
                  if (newValue?.category_key) {
                    handleSelectIndexCategory(newValue.category_key);
                  }
                }}
                isOptionEqualToValue={(a, b) => a.category_key === b.category_key}
                getOptionLabel={(c) =>
                  c?.label ? `${c.label} (${c.category_group})` : ''
                }
                filterOptions={(opts, state) => {
                  const q = state.inputValue.trim().toLowerCase();
                  if (!q) return opts;
                  return opts.filter(
                    (c) =>
                      String(c.label || '')
                        .toLowerCase()
                        .includes(q) ||
                      String(c.category_key || '')
                        .toLowerCase()
                        .includes(q) ||
                      String(c.category_group || '')
                        .toLowerCase()
                        .includes(q),
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search & pick category"
                    placeholder="Type to search…"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <SearchOutlinedIcon color="action" sx={{ mr: 0.5, ml: 0.5 }} />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                noOptionsText="No matching shop / service type"
              />
              <Button
                fullWidth
                sx={{ mt: 2 }}
                variant="outlined"
                size="small"
                onClick={loadIndexCategories}
              >
                Refresh list
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={9}>
          <Card variant="outlined">
            <CardContent>
              <Box display="flex" flexWrap="wrap" gap={2} alignItems="flex-start" mb={2}>
                <Autocomplete
                  size="small"
                  autoHighlight
                  sx={{ minWidth: 280, maxWidth: 400 }}
                  options={subsForCategory}
                  value={
                    subFilter === 'all'
                      ? null
                      : subsForCategory.find((s) => s.code === subFilter) || null
                  }
                  onChange={(_, newValue) => applySubFilter(newValue?.code ?? 'all')}
                  isOptionEqualToValue={(a, b) => a.code === b.code}
                  getOptionLabel={(s) => (s?.name_en ? `${s.name_en} (${s.code})` : '')}
                  filterOptions={(opts, state) => {
                    const q = state.inputValue.trim().toLowerCase();
                    if (!q) return opts;
                    return opts.filter(
                      (s) =>
                        s.code.toLowerCase().includes(q) || s.name_en.toLowerCase().includes(q),
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Find & pick shop type"
                      placeholder="Search all subs in this category…"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <SearchOutlinedIcon color="action" sx={{ mr: 0.5, ml: 0.5 }} />
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  noOptionsText="No matching shop type for this category"
                />
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel id="sub-label">Filter by shop type (sub)</InputLabel>
                  <Select
                    labelId="sub-label"
                    label="Filter by shop type (sub)"
                    value={subFilter}
                    onChange={(e) => applySubFilter(e.target.value)}
                    MenuProps={{
                      PaperProps: { sx: { maxHeight: 360 } },
                    }}
                  >
                    <MenuItem value="all">All sub-categories</MenuItem>
                    {subsForCategory.map((s) => (
                      <MenuItem key={s.code} value={s.code}>
                        {s.name_en} ({s.code})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={openCreateGroup}
                      disabled={!selectedCategory || subFilter === 'all'}
                    >
                      Create group
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={openAdd}
                      disabled={!selectedCategory || subFilter === 'all'}
                    >
                      Add product / service
                    </Button>
                  </Stack>
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.75, maxWidth: 420 }}>
                    {subFilter !== 'all' ? (
                      <>
                        Create a <strong>group</strong> first (e.g. Print Services), then add products
                        under{' '}
                        <strong>
                          {subsForCategory.find((s) => s.code === subFilter)?.name_en || subFilter}
                        </strong>{' '}
                        (<code>{selectedIndexKey || `${selectedCategory}__${subFilter}`}</code>).
                      </>
                    ) : (
                      <>Pick a shop type above to create groups and products for that sub-list only.</>
                    )}
                  </Typography>
                </Box>
              </Box>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                justifyContent="space-between"
                sx={{ mb: 1.5 }}
              >
                <TextField
                  size="small"
                  value={itemSearch}
                  onChange={(e) => setItemSearch(e.target.value)}
                  placeholder="Search products / services…"
                  sx={{ minWidth: { sm: 280 }, flex: 1, maxWidth: 420 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchOutlinedIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="caption" color="text.secondary">
                    {filteredItems.length} shown
                    {selectedIds.size ? ` · ${selectedIds.size} selected` : ''}
                    {!showRecycleBin && trashedCount
                      ? ` · ${trashedCount} in bin`
                      : ''}
                  </Typography>
                  <Button
                    size="small"
                    variant={showRecycleBin ? 'contained' : 'outlined'}
                    color={showRecycleBin ? 'warning' : 'inherit'}
                    startIcon={<DeleteOutlineIcon />}
                    onClick={() => setShowRecycleBin((v) => !v)}
                  >
                    {showRecycleBin ? 'Back to active list' : `Recycle Bin${trashedCount ? ` (${trashedCount})` : ''}`}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<SelectAllOutlinedIcon />}
                    disabled={!filteredItems.length}
                    onClick={toggleSelectAllVisible}
                  >
                    {allVisibleSelected ? 'Deselect all' : 'Select all'}
                  </Button>
                  {showRecycleBin ? (
                    <>
                      <Button
                        size="small"
                        variant="outlined"
                        color="success"
                        startIcon={
                          binBusy ? <CircularProgress size={14} /> : <RestoreFromTrashOutlinedIcon />
                        }
                        disabled={!selectedIds.size || binBusy}
                        onClick={restoreSelectedLines}
                      >
                        Restore
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={
                          binBusy ? <CircularProgress size={14} /> : <DeleteForeverOutlinedIcon />
                        }
                        disabled={!selectedIds.size || binBusy}
                        onClick={purgeSelectedLines}
                      >
                        Delete forever
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      startIcon={
                        bulkDeleting ? <CircularProgress size={14} /> : <DeleteOutlineIcon />
                      }
                      disabled={!selectedIds.size || bulkDeleting}
                      onClick={deleteSelectedLines}
                    >
                      Move to Recycle Bin
                    </Button>
                  )}
                </Stack>
              </Stack>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" width={48}>
                        <Checkbox
                          size="small"
                          checked={allVisibleSelected}
                          indeterminate={
                            selectedIds.size > 0 &&
                            !allVisibleSelected &&
                            filteredItems.some((row) => selectedIds.has(row.id))
                          }
                          onChange={toggleSelectAllVisible}
                          disabled={!filteredItems.length}
                          inputProps={{ 'aria-label': 'Select all visible products' }}
                        />
                      </TableCell>
                      <TableCell width={56}>Thumb</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Group → sub</TableCell>
                      <TableCell>Sub code</TableCell>
                      <TableCell>Unit</TableCell>
                      <TableCell>GST %</TableCell>
                      <TableCell>HSN</TableCell>
                      <TableCell align="right">Final ₹</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredItems.map((row) => {
                      const med =
                        row.pricing?.find((p) => p.region_code === 'DEFAULT')?.median_price ??
                        row.pricing?.[0]?.median_price ??
                        0;
                      const source =
                        row.pricing?.find((p) => p.region_code === 'DEFAULT')?.source ??
                        row.pricing?.[0]?.source ??
                        '';
                      const fromBeta = String(source).toLowerCase() === 'mitra_beta_tester';
                      const checked = selectedIds.has(row.id);
                      return (
                        <TableRow key={row.id} hover selected={checked}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              size="small"
                              checked={checked}
                              onChange={() => toggleSelectRow(row.id)}
                              inputProps={{ 'aria-label': `Select ${row.name_en}` }}
                            />
                          </TableCell>
                          <TableCell>
                            {row.thumbnail_url ? (
                              <Box
                                component="img"
                                src={row.thumbnail_url}
                                alt=""
                                sx={{
                                  width: 40,
                                  height: 40,
                                  objectFit: 'cover',
                                  borderRadius: 1,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                }}
                              />
                            ) : (
                              <Box
                                sx={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: 1,
                                  bgcolor: 'action.hover',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <Inventory2OutlinedIcon fontSize="small" color="disabled" />
                              </Box>
                            )}
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                              <Typography variant="body2" fontWeight={600}>
                                {row.name_en}
                              </Typography>
                              {fromBeta ? (
                                <Chip size="small" color="success" variant="outlined" label="Beta shared" />
                              ) : null}
                            </Stack>
                            {row.product_details ? (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  display: '-webkit-box',
                                  WebkitLineClamp: 1,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                }}
                              >
                                {row.product_details}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell>
                            <Chip size="small" label={row.item_type} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {row.main_category} → {row.sub_category}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <code>{row.subcategory_code}</code>
                          </TableCell>
                          <TableCell>{row.default_unit || '—'}</TableCell>
                          <TableCell>{row.gst_percent != null ? row.gst_percent : '—'}</TableCell>
                          <TableCell>{row.hsn_code || '—'}</TableCell>
                          <TableCell align="right">{Number(med).toFixed(2)}</TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                              {showRecycleBin ? (
                                <>
                                  <Button
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                    startIcon={<RestoreFromTrashOutlinedIcon sx={{ fontSize: 16 }} />}
                                    disabled={binBusy}
                                    onClick={async () => {
                                      setBinBusy(true);
                                      try {
                                        await api.post(`/admin/template-items/${row.id}/restore`);
                                        const res = await api.get(
                                          `/admin/categories/${encodeURIComponent(selectedCategory)}`,
                                        );
                                        if (res.data?.success) setDetail(res.data.data);
                                        setSnack({ severity: 'success', message: 'Restored.' });
                                      } catch (e) {
                                        setSnack({
                                          severity: 'error',
                                          message: e.response?.data?.message || e.message,
                                        });
                                      } finally {
                                        setBinBusy(false);
                                      }
                                    }}
                                  >
                                    Restore
                                  </Button>
                                  <Button
                                    size="small"
                                    color="error"
                                    variant="outlined"
                                    startIcon={<DeleteForeverOutlinedIcon sx={{ fontSize: 16 }} />}
                                    disabled={binBusy}
                                    onClick={async () => {
                                      if (
                                        !window.confirm(
                                          `Permanently delete "${row.name_en}"? This cannot be undone.`,
                                        )
                                      ) {
                                        return;
                                      }
                                      setBinBusy(true);
                                      try {
                                        await api.delete(
                                          `/admin/template-items/${row.id}?permanent=true`,
                                        );
                                        const res = await api.get(
                                          `/admin/categories/${encodeURIComponent(selectedCategory)}`,
                                        );
                                        if (res.data?.success) setDetail(res.data.data);
                                        setSnack({
                                          severity: 'success',
                                          message: 'Permanently deleted.',
                                        });
                                      } catch (e) {
                                        setSnack({
                                          severity: 'error',
                                          message: e.response?.data?.message || e.message,
                                        });
                                      } finally {
                                        setBinBusy(false);
                                      }
                                    }}
                                  >
                                    Delete forever
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
                                    onClick={() => openEdit(row)}
                                  >
                                    Edit
                                  </Button>
                                  <Button
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                    startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
                                    onClick={() => deleteLine(row.id, row.name_en)}
                                  >
                                    To Bin
                                  </Button>
                                </>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {!filteredItems.length && (
                      <TableRow>
                        <TableCell colSpan={11}>
                          <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                            {itemSearch.trim()
                              ? 'No products match your search.'
                              : showRecycleBin
                                ? 'Recycle Bin is empty for this filter.'
                                : 'No lines yet for this filter. Add lines or run DB migration seeds.'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth scroll="paper">
        <DialogTitle sx={{ pb: 1 }}>
          {dialogMode === 'add' ? 'Add product / service' : 'Edit template line'}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 400 }}>
            1 Group → 2 Name → 3 Unit → 4 Image → price → GST/HSN (optional)
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Autocomplete
            size="small"
            fullWidth
            autoHighlight
            options={subsForCategory}
            value={
              subsForCategory.find((s) => s.code === form.subcategory_code) || null
            }
            onChange={(_, newValue) =>
              setForm((f) => ({
                ...f,
                subcategory_code: newValue?.code || '',
                group_id: '',
                main_category: '',
              }))
            }
            isOptionEqualToValue={(a, b) => a.code === b.code}
            getOptionLabel={(s) => (s?.name_en ? `${s.name_en} (${s.code})` : '')}
            filterOptions={(opts, state) => {
              const q = state.inputValue.trim().toLowerCase();
              if (!q) return opts;
              return opts.filter(
                (s) =>
                  String(s.name_en || '')
                    .toLowerCase()
                    .includes(q) ||
                  String(s.code || '')
                    .toLowerCase()
                    .includes(q),
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Shop type (Citizen code)"
                placeholder="Search all shop types in this category…"
                required
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <SearchOutlinedIcon color="action" sx={{ mr: 0.5, ml: 0.5 }} />
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
            noOptionsText="No matching shop type"
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
            <Autocomplete
              size="small"
              fullWidth
              autoHighlight
              options={groupsForSub}
              value={groupsForSub.find((g) => String(g.id) === String(form.group_id)) || null}
              onChange={(_, newValue) =>
                setForm((f) => applyGroupDefaults(newValue?.id || '', f))
              }
              isOptionEqualToValue={(a, b) => String(a.id) === String(b.id)}
              getOptionLabel={(g) =>
                g?.name
                  ? `${g.name}${g.item_type ? ` · ${g.item_type}` : ''}${
                      g.default_unit ? ` · ${g.default_unit}` : ''
                    }`
                  : ''
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="1. Category / group *"
                  placeholder={groupsForSub.length ? 'Select group' : 'Create a group first'}
                  required
                  helperText="Mitra-style shelf group (Print Services, Training…)"
                />
              )}
              noOptionsText="No groups yet — click Create group"
            />
            <Button
              variant="outlined"
              sx={{ whiteSpace: 'nowrap', mt: { sm: 0.5 } }}
              onClick={openCreateGroup}
              disabled={!form.subcategory_code}
            >
              + Group
            </Button>
          </Stack>

          <TextField
            label="2. Product / service name *"
            value={form.name_en}
            onChange={(e) => setForm((f) => ({ ...f, name_en: e.target.value }))}
            fullWidth
            required
            placeholder="e.g. Black & White Print"
            helperText="Shown on bills, citizen cards, and Smart Bill import"
          />

          <FormControl fullWidth size="small">
            <InputLabel>Item type</InputLabel>
            <Select
              label="Item type"
              value={form.item_type}
              onChange={(e) => setForm((f) => ({ ...f, item_type: e.target.value }))}
            >
              <MenuItem value="product">Product</MenuItem>
              <MenuItem value="service">Service</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Product / service details (optional)"
            value={form.product_details}
            onChange={(e) => setForm((f) => ({ ...f, product_details: e.target.value }))}
            fullWidth
            multiline
            minRows={2}
            placeholder="Ingredients, portion, flavour, what’s included…"
            helperText="Used for the AI thumbnail prompt and catalog clarity"
          />

          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              border: '1px dashed',
              borderColor: 'primary.light',
              bgcolor: 'primary.50',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
              <AutoAwesomeIcon color="primary" fontSize="small" />
              <Typography variant="subtitle2" fontWeight={700}>
                AI thumbnail prompt
              </Typography>
              <Chip size="small" label={`${THUMB_PX}×${THUMB_PX} PNG`} />
            </Stack>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, mb: 1 }}>
              Generate → copy → paste in Gemini / ChatGPT → download → upload here. Prompt forces
              {THUMB_PX}×{THUMB_PX}, transparent PNG, ~KB file size (not multi‑MB).
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                size="small"
                variant="contained"
                startIcon={<AutoAwesomeIcon />}
                onClick={handleGeneratePrompt}
              >
                Auto generate prompt
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                onClick={handleCopyPrompt}
              >
                Copy
              </Button>
              <Button
                size="small"
                variant="text"
                endIcon={<OpenInNewIcon />}
                href="https://gemini.google.com/app"
                target="_blank"
                rel="noopener noreferrer"
              >
                Gemini
              </Button>
              <Button
                size="small"
                variant="text"
                endIcon={<OpenInNewIcon />}
                href="https://chatgpt.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                ChatGPT
              </Button>
            </Stack>
            {aiPrompt ? (
              <TextField
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                fullWidth
                multiline
                minRows={3}
                size="small"
                sx={{ mt: 1.5, bgcolor: 'background.paper' }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                      <Tooltip title="Copy prompt">
                        <IconButton size="small" onClick={handleCopyPrompt} edge="end">
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            ) : null}
          </Box>

          <Box>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Thumbnail
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 88,
                  height: 88,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  overflow: 'hidden',
                  bgcolor: 'grey.50',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {form.thumbnail_url ? (
                  <Box
                    component="img"
                    src={form.thumbnail_url}
                    alt="Thumbnail preview"
                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <Inventory2OutlinedIcon color="disabled" />
                )}
              </Box>
              <Stack spacing={1} flex={1}>
                <input
                  ref={thumbInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  hidden
                  onChange={handleThumbFile}
                />
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={
                    thumbUploading ? (
                      <CircularProgress size={16} />
                    ) : (
                      <CloudUploadOutlinedIcon />
                    )
                  }
                  disabled={thumbUploading}
                  onClick={() => thumbInputRef.current?.click()}
                >
                  {form.thumbnail_url ? 'Replace image' : 'Upload thumbnail'}
                </Button>
                {form.thumbnail_url ? (
                  <Button
                    size="small"
                    color="inherit"
                    onClick={() => setForm((f) => ({ ...f, thumbnail_url: '' }))}
                  >
                    Remove
                  </Button>
                ) : null}
                <Typography variant="caption" color="text.secondary">
                  Large AI dumps OK · auto-resized to {THUMB_PX}×{THUMB_PX} (~KB) on upload
                </Typography>
              </Stack>
            </Stack>
          </Box>

          {dialogMode === 'edit' && (
            <>
              <Divider />
              <TextField
                label="Shelf — sub line"
                value={form.sub_category}
                onChange={(e) => setForm((f) => ({ ...f, sub_category: e.target.value }))}
                fullWidth
                helperText="Optional finer label under the group"
              />
              <TextField
                label="Sort order"
                type="number"
                value={form.display_order}
                onChange={(e) => setForm((f) => ({ ...f, display_order: e.target.value }))}
                fullWidth
                inputProps={{ step: 1 }}
              />
            </>
          )}

          <Box>
            <TextField
              label="3. Unit *"
              value={form.default_unit}
              onChange={(e) => setForm((f) => ({ ...f, default_unit: e.target.value }))}
              fullWidth
              required
              placeholder="kg / litres / pieces"
              helperText="Quick picks below, or type your own"
            />
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
              {UNIT_PRESETS.map((u) => (
                <Chip
                  key={u}
                  size="small"
                  label={u}
                  variant={form.default_unit === u ? 'filled' : 'outlined'}
                  color={form.default_unit === u ? 'primary' : 'default'}
                  onClick={() => setForm((f) => ({ ...f, default_unit: u }))}
                />
              ))}
            </Stack>
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Price (₹) *"
              type="number"
              value={form.price}
              onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
              fullWidth
              required
              inputProps={{ min: 0, step: '0.01' }}
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
              }}
            />
            <TextField
              label="Discount %"
              type="number"
              value={form.discount_percent}
              onChange={(e) => setForm((f) => ({ ...f, discount_percent: e.target.value }))}
              fullWidth
              inputProps={{ min: 0, max: 100, step: '0.01' }}
              helperText="Optional"
            />
          </Stack>

          <Box>
            <TextField
              label="GST %"
              type="number"
              value={form.gst_percent}
              onChange={(e) => setForm((f) => ({ ...f, gst_percent: e.target.value }))}
              fullWidth
              inputProps={{ min: 0, step: '0.01' }}
              helperText="Optional · applied after discount (exclusive)"
            />
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
              {GST_PRESETS.map((g) => (
                <Chip
                  key={g}
                  size="small"
                  label={`${g}%`}
                  variant={String(form.gst_percent) === String(g) ? 'filled' : 'outlined'}
                  color={String(form.gst_percent) === String(g) ? 'primary' : 'default'}
                  onClick={() => setForm((f) => ({ ...f, gst_percent: String(g) }))}
                />
              ))}
            </Stack>
          </Box>

          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              bgcolor: 'success.50',
              borderColor: 'success.light',
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="baseline">
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Final price (auto)
                </Typography>
                <Typography variant="h5" fontWeight={800} color="success.dark">
                  ₹{priceBreakdown.finalPrice.toFixed(2)}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" align="right">
                After disc ₹{priceBreakdown.afterDiscount.toFixed(2)}
                <br />
                + GST ₹{priceBreakdown.gstAmount.toFixed(2)}
              </Typography>
            </Stack>
          </Paper>

          <TextField
            label="HSN / SAC (optional)"
            value={form.hsn_code}
            onChange={(e) => setForm((f) => ({ ...f, hsn_code: e.target.value }))}
            fullWidth
            placeholder="e.g. 1006, 9963"
          />
          <TextField
            label="Challana (optional)"
            type="number"
            value={form.challana}
            onChange={(e) => setForm((f) => ({ ...f, challana: e.target.value }))}
            fullWidth
            inputProps={{ min: 0, step: '0.01' }}
            helperText="Copied into Smart Bill on import (default 0)"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            variant="contained"
            disabled={thumbUploading}
            onClick={() => (dialogMode === 'add' ? submitAdd() : submitEdit())}
          >
            {dialogMode === 'add' ? 'Add' : 'Save changes'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={groupDialogOpen}
        onClose={() => !groupSaving && setGroupDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Create category / group</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Same as Mitra Operate: create the group first, then add products under it.
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel>Shop type</InputLabel>
            <Select
              label="Shop type"
              value={groupForm.subcategory_code}
              onChange={(e) =>
                setGroupForm((f) => ({ ...f, subcategory_code: e.target.value }))
              }
            >
              {subsForCategory.map((s) => (
                <MenuItem key={s.code} value={s.code}>
                  {s.name_en} ({s.code})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>Kind</InputLabel>
            <Select
              label="Kind"
              value={groupForm.item_type}
              onChange={(e) => setGroupForm((f) => ({ ...f, item_type: e.target.value }))}
            >
              <MenuItem value="product">Product</MenuItem>
              <MenuItem value="service">Service</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Group name *"
            value={groupForm.name}
            onChange={(e) => setGroupForm((f) => ({ ...f, name: e.target.value }))}
            fullWidth
            required
            placeholder="e.g. Print Services, Training"
          />
          <TextField
            label={groupForm.item_type === 'product' ? 'Default unit *' : 'Default unit'}
            value={groupForm.default_unit}
            onChange={(e) => setGroupForm((f) => ({ ...f, default_unit: e.target.value }))}
            fullWidth
            required={groupForm.item_type === 'product'}
            placeholder="piece / per page / job"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setGroupDialogOpen(false)} disabled={groupSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={submitCreateGroup} disabled={groupSaving}>
            {groupSaving ? 'Saving…' : 'Create group'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={6000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snack ? (
          <Alert onClose={() => setSnack(null)} severity={snack.severity || 'info'} sx={{ width: '100%' }}>
            {snack.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Container>
  );
};

export default CatalogTemplates;
